import { ReactNode, useMemo, useRef, useState } from 'react';
import { formatCount, formatDay } from '../../utils/period';

/**
 * Small, dependency-free SVG charts for the statistics screens. Colors are the
 * validated categorical slots defined as --series-N in Statistics.css, always
 * assigned in order and never cycled.
 */

export const SERIES_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)'];

/** A round axis maximum: 1, 2, 2.5 or 5 times a power of ten, at least `value`. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * power) return step * power;
  }
  return 10 * power;
}

export interface LineSeries {
  key: string;
  label: string;
}

interface LineChartProps {
  data: Record<string, any>[];
  series: LineSeries[];
  /** Key of the YYYY-MM-DD day on each row. */
  xKey?: string;
  height?: number;
  title?: string;
}

const W = 1000;
const PAD = { top: 16, right: 110, bottom: 36, left: 48 };

/** Day-by-day lines with a crosshair tooltip. One axis, shared by every series. */
export function LineChart({ data, series, xKey = 'day', height = 280, title }: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const innerW = W - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;

  const max = useMemo(
    () => niceMax(Math.max(0, ...data.flatMap((row) => series.map((s) => Number(row[s.key]) || 0)))),
    [data, series]
  );

  if (data.length === 0) return <p className="stats-empty">No data in this period.</p>;

  const x = (i: number) => PAD.left + (data.length === 1 ? innerW / 2 : (i * innerW) / (data.length - 1));
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH;
  const labelEvery = Math.ceil(data.length / 8);

  // Direct labels at the line ends, nudged apart so they never overlap.
  const ends = series
    .map((s, i) => ({ s, i, y: y(Number(data[data.length - 1][s.key]) || 0) }))
    .sort((a, b) => a.y - b.y);
  for (let k = 1; k < ends.length; k++) ends[k].y = Math.max(ends[k].y, ends[k - 1].y + 14);

  const onMove = (event: React.MouseEvent<SVGRectElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const px = ((event.clientX - box.left) / box.width) * W;
    const i = Math.round(((px - PAD.left) / innerW) * (data.length - 1));
    setHover(Math.min(data.length - 1, Math.max(0, i)));
  };

  return (
    <figure className="stats-chart">
      {title && <figcaption className="stats-chart-title">{title}</figcaption>}
      {series.length > 1 && (
        <ul className="stats-legend">
          {series.map((s, i) => (
            <li key={s.key}>
              <span className="stats-legend-swatch" style={{ background: SERIES_COLORS[i] }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div className="stats-chart-canvas">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${height}`} role="img" aria-label={title || series.map((s) => s.label).join(', ')}>
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <g key={f}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(max * f)} y2={y(max * f)} className={f === 0 ? 'stats-axis' : 'stats-gridline'} />
              <text x={PAD.left - 8} y={y(max * f) + 4} textAnchor="end" className="stats-tick">
                {formatCount(max * f)}
              </text>
            </g>
          ))}
          {data.map((row, i) =>
            i % labelEvery === 0 || i === data.length - 1 ? (
              <text key={i} x={x(i)} y={height - 12} textAnchor="middle" className="stats-tick">
                {formatDay(row[xKey])}
              </text>
            ) : null
          )}
          {series.map((s, si) => (
            <path
              key={s.key}
              d={data.map((row, i) => `${i ? 'L' : 'M'}${x(i)},${y(Number(row[s.key]) || 0)}`).join(' ')}
              className="stats-line"
              style={{ stroke: SERIES_COLORS[si] }}
            />
          ))}
          {ends.map(({ s, i, y: ly }) => (
            <text key={s.key} x={W - PAD.right + 8} y={ly + 4} className="stats-direct-label">
              {s.label}
            </text>
          ))}
          {hover !== null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} className="stats-crosshair" />
              {series.map((s, si) => (
                <circle
                  key={s.key}
                  cx={x(hover)}
                  cy={y(Number(data[hover][s.key]) || 0)}
                  r={5}
                  className="stats-marker"
                  style={{ fill: SERIES_COLORS[si] }}
                />
              ))}
            </g>
          )}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={innerW}
            height={innerH}
            className="stats-hit"
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          />
        </svg>
        {hover !== null && (
          <div
            className="stats-tooltip"
            style={{ left: `${(x(hover) / W) * 100}%`, transform: `translateX(${x(hover) > W / 2 ? '-105%' : '5%'})` }}
          >
            <div className="stats-tooltip-title">{formatDay(data[hover][xKey])}</div>
            {series.map((s, si) => (
              <div key={s.key} className="stats-tooltip-row">
                <span className="stats-legend-swatch" style={{ background: SERIES_COLORS[si] }} aria-hidden="true" />
                <span>{s.label}</span>
                <strong>{formatCount(Number(data[hover][s.key]) || 0)}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </figure>
  );
}

export interface BarItem {
  label: ReactNode;
  value: number;
  /** Text shown beside the bar; defaults to the count. */
  display?: string;
  /** A second line under the label, e.g. drop-off. */
  sub?: ReactNode;
}

/**
 * Horizontal bars for a funnel or distribution: one hue, values printed beside
 * each bar so no reading depends on the bar length alone.
 */
export function BarList({ items, max }: { items: BarItem[]; max?: number }) {
  const top = max ?? Math.max(1, ...items.map((i) => i.value));
  if (items.length === 0) return <p className="stats-empty">Nothing in this period.</p>;
  return (
    <ul className="stats-bars">
      {items.map((item, i) => (
        <li key={i} title={`${typeof item.label === 'string' ? item.label : ''}: ${item.display ?? formatCount(item.value)}`}>
          <div className="stats-bar-label">
            {item.label}
            {item.sub && <span className="stats-bar-sub">{item.sub}</span>}
          </div>
          <div className="stats-bar-track">
            <div className="stats-bar-fill" style={{ width: `${(item.value / top) * 100}%` }} />
          </div>
          <div className="stats-bar-value">{item.display ?? formatCount(item.value)}</div>
        </li>
      ))}
    </ul>
  );
}

interface HeatmapProps {
  rows: { key: string; label: string }[];
  columns: { key: string; label: string }[];
  value: (row: string, column: string) => number;
  /** Tooltip text for a cell. */
  describe?: (row: string, column: string) => string;
}

/** Sequential single-hue grid (light = few, dark = many), with each count printed in its cell. */
export function Heatmap({ rows, columns, value, describe }: HeatmapProps) {
  const max = Math.max(1, ...rows.flatMap((r) => columns.map((c) => value(r.key, c.key))));
  const step = (v: number) => (v === 0 ? 0 : Math.min(5, Math.ceil((v / max) * 5)));
  return (
    <div className="stats-table-scroll">
      <table className="stats-heatmap">
        <thead>
          <tr>
            <th />
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              {columns.map((c) => {
                const v = value(r.key, c.key);
                return (
                  <td key={c.key} className={`stats-heat-${step(v)}`} title={describe ? describe(r.key, c.key) : undefined}>
                    {formatCount(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
